// @ts-nocheck
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import FlashOffIcon from '@material-ui/icons/FlashOff';
import FlashOnIcon from '@material-ui/icons/FlashOn';
import SensorIcon from '@material-ui/icons/SettingsInputAntenna';
import WarningIcon from '@material-ui/icons/Warning';
import { Entity, ctx as L20NContext } from '@sketchpixy/rubix/lib/L20n';
import _ from 'lodash';
import React from 'react';
import Select from 'react-select';
import { connect } from 'react-redux';
import { Field, change, initialize, reduxForm } from 'redux-form';
import * as PowerControlUnitsActions from '../../redux/actions/powerControlUnits.actions';
import * as ModalActions from '../../redux/actions/modal.actions';
import { BLUE, GREEN, ORANGE, RED } from '../../_config/consts';
import * as LockActions from '../../redux/actions/lock.actions';
import * as TagActions from '../../redux/actions/tag.actions';
import * as UtilsActions from '../../redux/actions/utils.actions';
import MapSelectionField from '../forms/Fields/MapSelectionField.jsx';
import CustomField from '../forms/Fields/CustomField.jsx';
import MDCheckBoxField from '../forms/Fields/MDCheckBox/MDCheckBoxField.js';
import SentenceSelectorInputField from '../forms/Fields/SentenceSelectorInputField.jsx';
import TagsSelectorField from '../forms/Fields/TagsSelectorField.jsx';
import FormFlatContainer from '../forms/Elements/FormFlatContainer.jsx';
import MDRadioButtonsGroupField from '../forms/Elements/MDRadioButtonsGroup/MDRadioButtonsGroupField.js';
import MDSwitchView from '../forms/Fields/MDSwitch/MDSwitchView.jsx';
import MDButton from '../MDButton/MDButton.jsx';
import InfoSection from '../InfoSection/InfoSection.jsx';

const sectionIconStyle = color => ({ fontSize: 26, color });

const ACTUATORS_MODE = {
  ALL: 'ALL',
  LIMITED: 'LIMITED',
};

// --- Form helpers: PCU API shape <-> redux-form values ---

function dtoCheckbox(value) {
  return value === true;
}

function dtoMinutesField(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  return value;
}

function mapTriggerLockIdsToFormValues(ids, catalog, legacyLocks) {
  if (legacyLocks && legacyLocks.length > 0) {
    return legacyLocks;
  }
  const idList = ids || [];
  if (idList.length === 0) {
    return [];
  }
  const locks = catalog || [];
  return _.map(idList, (id) => {
    const match = _.find(locks, lock => lock.id === id || String(lock.id) === String(id));
    if (match) {
      return { id: match.id, name: match.name };
    }
    return { id, name: String(id) };
  });
}

function mapActuateUserTagsFromPcu(source, guestTagsCatalog) {
  const tags = source.actuateUserTags;
  if (Array.isArray(tags) && tags.length > 0) {
    return tags;
  }
  const tagIds = source.actuatorTagIds || source.actuateUserTagIds;
  if (!Array.isArray(tagIds) || tagIds.length === 0) {
    return [];
  }
  const catalog = guestTagsCatalog || [];
  return _.map(tagIds, (id) => {
    const match = _.find(catalog, tag => tag.id === id || String(tag.id) === String(id));
    if (match) {
      return { id: match.id, name: match.name, type: match.type || 'user' };
    }
    return { id, name: String(id), type: 'user' };
  });
}

function mapGeolocationFromPcu(source) {
  const lat = source.latitude;
  const lng = source.longitude;
  if (lat != null && lat !== '' && lng != null && lng !== '') {
    return {
      latitude: lat,
      longitude: lng,
      radiusMeters: source.geofenceRadiusMeters ?? 200,
    };
  }
  return null;
}

// Trigger-lock pickers need lock names; merge PCU payload with Redux lock list.
function mergeTriggerLockCatalog(source, lockCatalog) {
  const pcuTriggerLocks = source && source.triggerLocks ? source.triggerLocks : [];
  const catalogLocks = _.map(lockCatalog || [], lock => ({
    id: lock.id,
    name: lock.name,
  }));
  return _.uniqBy([...pcuTriggerLocks, ...catalogLocks], lock => lock.id);
}

function mapPcuToAutomationFormValues(pcu, guestTagsCatalog, lockCatalog) {
  // Initial values for PowerControlUnitAutomationForm when opening or refreshing a PCU.
  const source = pcu || {};
  const triggerLockCatalog = mergeTriggerLockCatalog(source, lockCatalog);
  const actuatorTagIds = source.actuatorTagIds;
  const hasActuatorTags = Array.isArray(actuatorTagIds) && actuatorTagIds.length > 0;
  const hasActuatorsLimits = hasActuatorTags
    || dtoCheckbox(source.actuateByUserTags)
    || dtoCheckbox(source.guestActuateByLockCredential)
    || dtoCheckbox(source.geolocationCheck);
  return {
    geolocation: mapGeolocationFromPcu(source),
    hyperTagId: source.hyperTagId ?? '',
    automationEnabled: source.automationEnabled === undefined
      ? 'true'
      : (source.automationEnabled ? 'true' : 'false'),
    powerOnByHyperTag: dtoCheckbox(source.powerOnByHyperTag),
    powerOnByTriggerLock: dtoCheckbox(source.powerOnByTriggerLock),
    powerOnByPresence: dtoCheckbox(source.powerOnByPresence),
    powerOffByHyperTag: dtoCheckbox(source.powerOffByHyperTag),
    powerOffByTriggerLock: dtoCheckbox(source.powerOffByTriggerLock),
    powerOffByPresenceLost: dtoCheckbox(source.powerOffByPresenceLost),
    turnOffAfterPowerOnEnabled: (source.maxOnMinutes ?? 0) > 0,
    maxOnMinutes: dtoMinutesField(source.maxOnMinutes),
    gracePeriodMinutes: dtoMinutesField(source.gracePeriodMinutes),
    powerOffOnOpenGate: dtoCheckbox(source.powerOffOnOpenGate),
    geolocationCheck: dtoCheckbox(source.geolocationCheck),
    triggerLocksOn: mapTriggerLockIdsToFormValues(
      source.triggerLockOnIds,
      triggerLockCatalog,
      source.triggerLocksOn,
    ),
    triggerLocksOff: mapTriggerLockIdsToFormValues(
      source.triggerLockOffIds,
      triggerLockCatalog,
      source.triggerLocksOff,
    ),
    actuateByUserTags: hasActuatorTags || dtoCheckbox(source.actuateByUserTags),
    actuateUserTags: mapActuateUserTagsFromPcu(source, guestTagsCatalog),
    guestActuateByLockCredential: dtoCheckbox(source.guestActuateByLockCredential),
    actuatorsMode: hasActuatorsLimits ? ACTUATORS_MODE.LIMITED : ACTUATORS_MODE.ALL,
  };
}

function rulesMinutesField(value) {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  const parsed = Number(value);
  if (Number.isFinite(parsed)) {
    return parsed;
  }
  return 0;
}

function parseGeoCoordinate(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const normalized = String(value).trim().replace(/\s/g, '').replace(',', '.');
  const parsed = parseFloat(normalized);
  if (Number.isFinite(parsed)) {
    return parsed;
  }
  return null;
}

// PUT /powerControlUnits/{id}/rules
function mapFormValuesToRulesDto(formValues) {
  const values = formValues || {};
  const maxOnMinutes = values.turnOffAfterPowerOnEnabled === true
    ? rulesMinutesField(values.maxOnMinutes)
    : 0;
  const gracePeriodMinutes = rulesMinutesField(values.gracePeriodMinutes);

  return {
    powerOnByHyperTag: values.powerOnByHyperTag === true,
    powerOnByTriggerLock: values.powerOnByTriggerLock === true,
    powerOnByPresence: values.powerOnByPresence === true,
    powerOffByHyperTag: values.powerOffByHyperTag === true,
    powerOffByTriggerLock: values.powerOffByTriggerLock === true,
    powerOffByPresenceLost: values.powerOffByPresenceLost === true,
    powerOffOnOpenGate: values.powerOffOnOpenGate === true,
    // API omits zero minutes; baseline and save must use the same shape for diff to work.
    ...(maxOnMinutes > 0 ? { maxOnMinutes } : {}),
    ...(gracePeriodMinutes > 0 ? { gracePeriodMinutes } : {}),
  };
}

// PUT /powerControlUnits/{id}/actuators
function mapFormValuesToActuatorsDto(formValues) {
  const values = formValues || {};
  const radiusSource = values.geolocation && values.geolocation.radiusMeters;
  const geofenceRadiusMeters = rulesMinutesField(radiusSource);
  // With "every valid user" selected the limits are cleared, even if the hidden checkboxes are still set
  const isLimited = values.actuatorsMode === ACTUATORS_MODE.LIMITED;

  return {
    actuatorTagIds: isLimited && values.actuateByUserTags === true
      ? _.map(values.actuateUserTags || [], tag => tag.id)
      : [],
    guestActuateByLockCredential: isLimited && values.guestActuateByLockCredential === true,
    geolocationCheck: isLimited && values.geolocationCheck === true,
    geofenceRadiusMeters: geofenceRadiusMeters > 0 ? geofenceRadiusMeters : 200,
  };
}

// PUT /powerControlUnits/{id} — geolocation coordinates live on the PCU resource.
function mapFormValuesToPcuLocationDto(formValues) {
  const values = formValues || {};
  const isLimited = values.actuatorsMode === ACTUATORS_MODE.LIMITED;
  if (!isLimited || values.geolocationCheck !== true) {
    return null;
  }
  const geoLat = parseGeoCoordinate(values.geolocation && values.geolocation.latitude);
  const geoLng = parseGeoCoordinate(values.geolocation && values.geolocation.longitude);
  if (geoLat == null || geoLng == null) {
    return null;
  }
  return {
    latitude: geoLat,
    longitude: geoLng,
  };
}

// PUT /powerControlUnits/{id}/devices — trigger locks from form; other hardware ids from PCU (unchanged here).
function mapFormValuesToDevicesDto(formValues, selectedPCU) {
  const values = formValues || {};
  const pcu = selectedPCU || {};
  const devices = {
    relayAccessoryIds: pcu.relayAccessoryIds || [],
    doorSensorAccessoryIds: pcu.doorSensorAccessoryIds || [],
    // When the related rule checkbox is off, send empty ids (server clears association).
    triggerLockOnIds: values.powerOnByTriggerLock === true
      ? _.map(values.triggerLocksOn || [], lock => lock.id)
      : [],
    triggerLockOffIds: values.powerOffByTriggerLock === true
      ? _.map(values.triggerLocksOff || [], lock => lock.id)
      : [],
  };
  if (pcu.presenceSensorAccessoryId !== undefined && pcu.presenceSensorAccessoryId !== null) {
    devices.presenceSensorAccessoryId = pcu.presenceSensorAccessoryId;
  }
  return devices;
}

// Single place to build the three API bodies from current form state (used for save + diff baseline).
function buildAutomationPayloads(formValues, selectedPCU) {
  return {
    rules: mapFormValuesToRulesDto(formValues),
    actuators: mapFormValuesToActuatorsDto(formValues),
    devices: mapFormValuesToDevicesDto(formValues, selectedPCU),
    pcuLocation: mapFormValuesToPcuLocationDto(formValues),
  };
}

function hasAutomationFormValues(automationForm) {
  return !!(automationForm && automationForm.values && Object.keys(automationForm.values).length > 0);
}

// Tag multi-select order may differ; compare actuators ignoring actuatorTagIds order.
function actuatorsDtoEqual(left, right) {
  if (left === right) {
    return true;
  }
  if (!left || !right) {
    return false;
  }
  return left.guestActuateByLockCredential === right.guestActuateByLockCredential
    && left.geolocationCheck === right.geolocationCheck
    && left.geofenceRadiusMeters === right.geofenceRadiusMeters
    && _.isEqual(_.sortBy(left.actuatorTagIds || []), _.sortBy(right.actuatorTagIds || []));
}

function pcuLocationDtoEqual(left, right) {
  return _.isEqual(left || null, right || null);
}

function TriggerLocksMultiSelect({ input, locks, disabled }) {
  const options = _.map(locks, lock => ({
    value: lock.id,
    label: lock.name,
    id: lock.id,
    name: lock.name,
  }));
  const value = _.map(input.value || [], lock => ({
    value: lock.id,
    label: lock.name,
    id: lock.id,
    name: lock.name,
  }));

  return (
    <div style={{ marginTop: 10, marginLeft: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
        <h5 style={{ margin: 0, fontSize: 13, color: '#666' }}>
          <Entity entity="triggerLocks" />
        </h5>
      </div>
      <Select
        multi
        value={value}
        options={options}
        onChange={(selected) => {
          input.onChange(_.map(selected || [], item => ({ id: item.id, name: item.name })));
        }}
        disabled={disabled}
        placeholder={L20NContext.getSync('selectTriggerLocks')}
      />
    </div>
  );
}

@connect(state => ({
  selectedPCU: state.powerControlUnits.selectedPCU,
  automationForm: state.form.PowerControlUnitAutomationForm,
  userTags: state.tags.user,
  locks: state.locks.data.content,
}))
@reduxForm({
  form: 'PowerControlUnitAutomationForm',
  destroyOnUnmount: false,
})
class PowerControlUnitAutomationView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};
    // Last saved/loaded API payloads; Save compares form -> next vs this baseline to skip unchanged PUTs.
    this.automationBaseline = null;
    this.lastInitedAutomationPcuId = null;
  }

  componentDidMount() {
    const { dispatch, selectedPCU, userTags, locks } = this.props;
    // Catalogs are shared in Redux — avoid refetching when another screen already loaded them.
    const hasGuestTags = userTags && userTags.data && userTags.data.length > 0;
    const hasLocks = locks && locks.length > 0;
    if (!hasGuestTags) {
      dispatch(TagActions.fetchGuestTags(false, null, 0, 200));
    }
    if (!hasLocks) {
      dispatch(LockActions.fetchLocks(0, false, 200));
    }
    this.initAutomationForm(selectedPCU, this.props, 'mount');
  }

  componentWillReceiveProps(nextProps) {
    const { selectedPCU: pcuOld, locks: locksOld } = this.props;
    const { selectedPCU: pcu, locks: locksNext } = nextProps;
    const oldId = pcuOld && pcuOld.id;
    const newId = pcu && pcu.id;
    // When locks arrive async, re-init so trigger lock labels resolve and baseline stays in sync.
    const locksLoaded = (!locksOld || locksOld.length === 0) && locksNext && locksNext.length > 0;
    if (newId !== oldId || locksLoaded) {
      if (newId !== oldId) {
        this.lastInitedAutomationPcuId = null;
      }
      const reinitReason = newId !== oldId ? 'pcuIdChanged' : 'locksLoaded';
      this.initAutomationForm(pcu, nextProps, reinitReason);
    }
  }

  syncAutomationBaseline(pcu, props = this.props) {
    const { userTags, locks } = props;
    const guestTagsCatalog = userTags && userTags.data ? userTags.data : [];
    const formValues = mapPcuToAutomationFormValues(pcu, guestTagsCatalog, locks || []);
    this.automationBaseline = buildAutomationPayloads(formValues, pcu);
  }

  initAutomationForm(pcu, props = this.props, reason = 'unknown') {
    const { dispatch, userTags, locks, automationForm } = props;
    const guestTagsCatalog = userTags && userTags.data ? userTags.data : [];
    const hasExistingForm = hasAutomationFormValues(automationForm);
    const pcuId = pcu && pcu.id;
    const forceReinit = reason === 'afterSuccessfulSave'
      || reason === 'afterAutomationToggle'
      || reason === 'pcuIdChanged'
      || reason === 'locksLoaded';

    if (!pcuId) {
      if (hasExistingForm) {
        return;
      } else {
        dispatch(initialize('PowerControlUnitAutomationForm', mapPcuToAutomationFormValues(
          {},
          guestTagsCatalog,
          locks || [],
        )));
        this.syncAutomationBaseline({}, props);
        this.lastInitedAutomationPcuId = null;
      }
    } else if (!forceReinit && reason === 'mount' && this.lastInitedAutomationPcuId === pcuId && hasExistingForm) {
      return;
    } else {
      dispatch(initialize('PowerControlUnitAutomationForm', mapPcuToAutomationFormValues(
        pcu,
        guestTagsCatalog,
        locks || [],
      )));
      this.syncAutomationBaseline(pcu, props);
      this.lastInitedAutomationPcuId = pcuId;
    }
  }

  // Enabled/disabled uses PUT .../automation immediately (not part of Save diff).
  async onAutomationEnabledChange(nextValue) {
    const { dispatch, selectedPCU, automationForm } = this.props;
    if (!selectedPCU || !selectedPCU.id) {
      return;
    }
    const previousValue = automationForm && automationForm.values
      ? automationForm.values.automationEnabled
      : 'true';
    const enabled = nextValue === 'true' || nextValue === true;
    if (enabled === selectedPCU.automationEnabled) {
      return;
    }
    dispatch(UtilsActions.setSpinnerVisibile(true));
    try {
      const result = await dispatch(PowerControlUnitsActions.updateAutomation(selectedPCU.id, enabled));
      dispatch(PowerControlUnitsActions.selectPowerControlUnit(result));
      this.initAutomationForm(result, this.props, 'afterAutomationToggle');
    } catch (error) {
      dispatch(change('PowerControlUnitAutomationForm', 'automationEnabled', previousValue));
      dispatch(ModalActions.showModal({
        modalType: 'ERROR_ALERT',
        modalProps: {
          message: (<h6 className="snack-title"><Entity entity="commandFailed" /></h6>),
        },
      }));
    } finally {
      dispatch(UtilsActions.setSpinnerVisibile(false));
    }
  }

  onGeolocationCheckChange(enabled) {
    const { dispatch, automationForm } = this.props;
    if (!enabled) {
      dispatch(change('PowerControlUnitAutomationForm', 'geolocation', null));
    } else {
      const geo = automationForm && automationForm.values ? automationForm.values.geolocation : null;
      if (!geo) {
        dispatch(change('PowerControlUnitAutomationForm', 'geolocation', { radiusMeters: 200 }));
      }
    }
  }

  onGeolocationCoordinateBlur(fieldName, rawValue) {
    const { dispatch } = this.props;
    const parsed = parseGeoCoordinate(rawValue);
    if (parsed == null) {
      return;
    }
    dispatch(change('PowerControlUnitAutomationForm', fieldName, parsed));
  }

  async onSaveAutomationRules() {
    const { dispatch, selectedPCU, automationForm, onSubmit: onSubmitCreate } = this.props;
    const formValues = automationForm && automationForm.values ? automationForm.values : {};
    const hasPcuId = selectedPCU && selectedPCU.id;
    if (hasPcuId) {
      // Diff-based save: only PUT endpoints whose slice changed since last init/successful save.
      const baseline = this.automationBaseline || buildAutomationPayloads(formValues, selectedPCU);
      const next = buildAutomationPayloads(formValues, selectedPCU);
      const saveEntries = [];
      if (!_.isEqual(next.rules, baseline.rules)) {
        saveEntries.push({
          promise: dispatch(PowerControlUnitsActions.updateRules(selectedPCU.id, next.rules)),
        });
      }
      if (!actuatorsDtoEqual(next.actuators, baseline.actuators)) {
        saveEntries.push({
          promise: dispatch(PowerControlUnitsActions.updateActuators(selectedPCU.id, next.actuators)),
        });
      }
      if (!_.isEqual(next.devices, baseline.devices)) {
        saveEntries.push({
          promise: dispatch(PowerControlUnitsActions.updateDevices(selectedPCU.id, next.devices)),
        });
      }
      if (!pcuLocationDtoEqual(next.pcuLocation, baseline.pcuLocation) && next.pcuLocation) {
        saveEntries.push({
          promise: dispatch(PowerControlUnitsActions.editPowerControlUnit(
            selectedPCU.id,
            next.pcuLocation,
          )),
        });
      }
      if (saveEntries.length === 0) {
        dispatch(ModalActions.showModal({
          modalType: 'SUCCESS_ALERT',
          modalProps: {
            message: (<h6 className="snack-title"><Entity entity="pcuNoAutomationChanges" /></h6>),
          },
        }));
        return;
      }
      dispatch(UtilsActions.setSpinnerVisibile(true));
      try {
        const saveResults = await Promise.all(_.map(saveEntries, entry => entry.promise));
        // Each PUT returns a partial PCU; merge onto current selection (order matches saveEntries).
        let result = { ...selectedPCU };
        saveResults.forEach((partial) => {
          result = { ...result, ...partial };
        });
        dispatch(PowerControlUnitsActions.selectPowerControlUnit(result));
        this.initAutomationForm(result, this.props, 'afterSuccessfulSave');
        dispatch(ModalActions.showModal({
          modalType: 'SUCCESS_ALERT',
          modalProps: {
            message: (<h6 className="snack-title"><Entity entity="pcuSaveSuccess" /></h6>),
          },
        }));
      } catch (error) {
        dispatch(ModalActions.showModal({
          modalType: 'ERROR_ALERT',
          modalProps: {
            message: (<h6 className="snack-title"><Entity entity="pcuSaveError" /></h6>),
          },
        }));
      } finally {
        dispatch(UtilsActions.setSpinnerVisibile(false));
      }
      return;
    }
    if (onSubmitCreate) {
      onSubmitCreate(formValues);
    }
  }

  // Options for power-on/off trigger lock multi-select (Redux locks + PCU + current form selection).
  getAvailableTriggerLocks() {
    const { selectedPCU, automationForm, locks } = this.props;
    const formValues = automationForm && automationForm.values ? automationForm.values : {};
    const catalogLocks = _.map(locks || [], lock => ({
      id: lock.id,
      name: lock.name,
    }));
    const pcuLocks = _.map(
      selectedPCU && selectedPCU.triggerLocks ? selectedPCU.triggerLocks : [],
      lock => ({ id: lock.id, name: lock.name }),
    );
    const formLocks = [
      ...(formValues.triggerLocksOn || []),
      ...(formValues.triggerLocksOff || []),
    ];
    return _.uniqBy([...catalogLocks, ...pcuLocks, ...formLocks], lock => lock.id);
  }

  renderUltraBadge(isUltra) {
    if (isUltra) {
      return null;
    }
    return (
      <span style={{ marginLeft: 8, color: '#ff9800', fontSize: 12, fontWeight: 'bold' }}>ULTRA</span>
    );
  }

  render() {
    const {
      selectedPCU,
      automationForm,
      dispatch,
      userTags,
    } = this.props;
    const formValues = automationForm && automationForm.values ? automationForm.values : {};
    const isUltra = dispatch(UtilsActions.isEnergyManagementUltra());
    const automationEnabled = formValues.automationEnabled === undefined
      ? true
      : formValues.automationEnabled === 'true' || formValues.automationEnabled === true;
    const rulesFieldsDisabled = !automationEnabled;
    const ultraFieldsDisabled = !isUltra;
    const ultraRuleDisabled = rulesFieldsDisabled || ultraFieldsDisabled;
    const doorSensorAccessoryIds = (selectedPCU && selectedPCU.doorSensorAccessoryIds) || [];
    const hasDoorSensorAccessories = doorSensorAccessoryIds.length > 0;
    const hyperSensorSectionDisabled = rulesFieldsDisabled || !hasDoorSensorAccessories;
    const rulesSectionContainerStyle = {
      backgroundColor: 'white',
      opacity: automationEnabled ? 1 : 0.5,
    };
    const hyperSensorSectionContainerStyle = {
      backgroundColor: 'white',
    };
    const hyperSensorFieldsContainerStyle = {
      opacity: hyperSensorSectionDisabled ? 0.5 : 1,
      pointerEvents: hyperSensorSectionDisabled ? 'none' : 'auto',
    };
    const rulesColumnStyle = {
      flex: '1 1 0',
      minWidth: 300,
      display: 'flex',
      flexDirection: 'column',
      gap: 15,
      pointerEvents: automationEnabled ? 'auto' : 'none',
    };
    const availableTriggerLocks = this.getAvailableTriggerLocks();

    const isActuatorsLimited = formValues.actuatorsMode === ACTUATORS_MODE.LIMITED;
    const actuatorsModeOptions = [
      { value: ACTUATORS_MODE.ALL, label: <Entity entity="actuatorsModeAll" /> },
      { value: ACTUATORS_MODE.LIMITED, label: <Entity entity="actuatorsModeLimited" /> },
    ];

    const checkBoxTitleStyle = { fontSize: 16, fontWeight: 'bold', color: '#3f3f3f' };

    return (
      <FormFlatContainer containerStyle={{ marginBottom: 100 }}>
        <div autoComplete="off">
          <div style={{ marginRight: 10 }}>
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 0,
              flexWrap: 'wrap',
              gap: 15,
            }}
            >
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <h5 style={{ margin: 0, marginRight: 15, fontSize: 18, marginBottom: 15, color: '#3f3f3f', fontWeight: 'bold' }}>
                  <Entity entity="automationRules" />
                </h5>
                <MDSwitchView
                  checked={automationEnabled}
                  label={<Entity entity={automationEnabled ? 'enabled' : 'disabled'} key={`automationSwitch-${automationEnabled}`} />}
                  titleStyle={{ fontSize: 15, fontWeight: 'bold', color: '#3f3f3f' }}
                  containerStyle={{ marginBottom: 8 }}
                  onSelect={(enabled) => {
                    dispatch(change('PowerControlUnitAutomationForm', 'automationEnabled', enabled ? 'true' : 'false'));
                    this.onAutomationEnabledChange(enabled);
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'row', gap: 15, alignItems: 'stretch', flexWrap: 'wrap' }}>
              <div style={rulesColumnStyle}>
                <FormFlatContainer
                  title="powerOnWhen"
                  icon={<FlashOnIcon style={sectionIconStyle(GREEN)} />}
                  containerStyle={{ ...rulesSectionContainerStyle, flex: 1 }}
                >
                  <Field
                    name="powerOnByHyperTag"
                    label={<Entity entity="turnOnWithHyperTag" />}
                    titleStyle={checkBoxTitleStyle}
                    component={MDCheckBoxField}
                    disabled={rulesFieldsDisabled}
                    containerstyle={{ marginTop: 0 }}
                  />
                  <Field
                    name="powerOnByTriggerLock"
                    label={<Entity entity="autoTurnOnTriggerLock" />}
                    titleStyle={checkBoxTitleStyle}
                    component={MDCheckBoxField}
                    disabled={rulesFieldsDisabled}
                    containerstyle={{ marginTop: 10 }}
                  />
                  {formValues.powerOnByTriggerLock && (
                    <Field
                      name="triggerLocksOn"
                      component={TriggerLocksMultiSelect}
                      locks={availableTriggerLocks}
                      disabled={rulesFieldsDisabled}
                    />
                  )}
                  <Field
                    name="powerOnByPresence"
                    label={
                      <span>
                        <Entity entity="autoTurnOnPresence" />
                        {this.renderUltraBadge(isUltra)}
                      </span>
                    }
                    titleStyle={checkBoxTitleStyle}
                    component={MDCheckBoxField}
                    disabled={ultraRuleDisabled}
                    containerstyle={{ marginTop: 10 }}
                  />
                </FormFlatContainer>
              </div>

              <div style={rulesColumnStyle}>
                <FormFlatContainer
                  title="powerOffWhen"
                  icon={<FlashOffIcon style={sectionIconStyle(RED)} />}
                  containerStyle={{ ...rulesSectionContainerStyle, flex: 1 }}
                >
                  <Field
                    name="powerOffByHyperTag"
                    label={<Entity entity="turnOffWithHyperTag" />}
                    titleStyle={checkBoxTitleStyle}
                    component={MDCheckBoxField}
                    disabled={rulesFieldsDisabled}
                    containerstyle={{ marginTop: 0 }}
                  />
                  <Field
                    name="powerOffByTriggerLock"
                    label={<Entity entity="autoTurnOffTriggerLock" />}
                    titleStyle={checkBoxTitleStyle}
                    component={MDCheckBoxField}
                    disabled={rulesFieldsDisabled}
                    containerstyle={{ marginTop: 10 }}
                  />
                  {formValues.powerOffByTriggerLock && (
                    <Field
                      name="triggerLocksOff"
                      component={TriggerLocksMultiSelect}
                      locks={availableTriggerLocks}
                      disabled={rulesFieldsDisabled}
                    />
                  )}
                  <Field
                    name="powerOffByPresenceLost"
                    label={
                      <span>
                        <Entity entity="autoTurnOffPresenceNotDetected" />
                        {this.renderUltraBadge(isUltra)}
                      </span>
                    }
                    titleStyle={checkBoxTitleStyle}
                    component={MDCheckBoxField}
                    disabled={ultraRuleDisabled}
                    containerstyle={{ marginTop: 10 }}
                  />
                  {formValues.powerOffByPresenceLost && (
                    <div style={{ marginLeft: 32, marginTop: 5 }}>
                      <Field
                        name="gracePeriodMinutes"
                        component={SentenceSelectorInputField}
                        unit="minutes"
                        disabled={ultraRuleDisabled}
                        titleStyle={{ fontSize: 15, fontWeight: 'bold' }}
                        sentence={L20NContext.getSync('gracePeriodMinutesSentence')}
                      />
                    </div>
                  )}
                  <Field
                    name="turnOffAfterPowerOnEnabled"
                    label={<Entity entity="autoTurnOffAfterPowerOn" />}
                    titleStyle={checkBoxTitleStyle}
                    component={MDCheckBoxField}
                    disabled={rulesFieldsDisabled}
                    containerstyle={{ marginTop: 10 }}
                  />
                  {formValues.turnOffAfterPowerOnEnabled && (
                    <div style={{ marginLeft: 32, marginTop: 5 }}>
                      <Field
                        name="maxOnMinutes"
                        component={SentenceSelectorInputField}
                        unit="minutes"
                        titleStyle={{ fontSize: 15, fontWeight: 'bold' }}
                        disabled={rulesFieldsDisabled}
                        sentence={L20NContext.getSync('maxOnMinutesFromPowerOnSentence')}
                      />
                    </div>
                  )}
                </FormFlatContainer>
              </div>
            </div>
            <FormFlatContainer
              title="hyperSensorInteraction"
              icon={<SensorIcon style={sectionIconStyle(BLUE)} />}
              containerStyle={hyperSensorSectionContainerStyle}
            >
              <div style={hyperSensorFieldsContainerStyle}>
                <Field
                  name="powerOffOnOpenGate"
                  label={<Entity entity="turnOffOnOpenGate" />}
                  titleStyle={checkBoxTitleStyle}
                  component={MDCheckBoxField}
                  disabled={hyperSensorSectionDisabled}
                  containerstyle={{ marginTop: 0 }}
                />
              </div>
              {!hasDoorSensorAccessories && (
                <InfoSection
                  title="noHyperSensorAssociatedWithPowerControlUnit"
                  gravity="warning"
                />
              )}
            </FormFlatContainer>
            <div style={{ ...rulesColumnStyle, minWidth: 0 }}>
              <FormFlatContainer
                title="whoCanActuate"
                icon={<PeopleOutlineIcon style={sectionIconStyle(BLUE)} />}
                containerStyle={rulesSectionContainerStyle}
              >
                <Field
                  name="actuatorsMode"
                  component={MDRadioButtonsGroupField}
                  containerstyle={{ marginTop: 0 }}
                  options={actuatorsModeOptions}
                  disabled={rulesFieldsDisabled}
                />
                {isActuatorsLimited && (
                  <div style={{ marginLeft: 32, marginTop: 5 }}>
                    <Field
                      name="actuateByUserTags"
                      label={<Entity entity="onlyUsersWithFollowingTags" />}
                      titleStyle={checkBoxTitleStyle}
                      component={MDCheckBoxField}
                      disabled={rulesFieldsDisabled}
                      containerstyle={{ marginTop: 0 }}
                    />
                    {formValues.actuateByUserTags && (
                      <div style={{ marginLeft: 32, marginTop: 10 }}>
                        <Field
                          name="actuateUserTags"
                          multi
                          placeholder={L20NContext.getSync('insertGuestTags')}
                          component={TagsSelectorField}
                          tagType="GUEST"
                          tags={userTags && userTags.data ? userTags.data : []}
                          isDisabled={rulesFieldsDisabled}
                        />
                      </div>
                    )}
                    <Field
                      name="guestActuateByLockCredential"
                      label={<Entity entity="guestActuateByLockCredential" />}
                      titleStyle={checkBoxTitleStyle}
                      component={MDCheckBoxField}
                      disabled={rulesFieldsDisabled}
                      containerstyle={{ marginTop: 10 }}
                    />
                    <Field
                      name="geolocationCheck"
                      label={<Entity entity="geolocationCheckRequired" />}
                      titleStyle={checkBoxTitleStyle}
                      component={MDCheckBoxField}
                      disabled={rulesFieldsDisabled}
                      containerstyle={{ marginTop: 10 }}
                      onHandleChange={(value) => this.onGeolocationCheckChange(value)}
                    />
                    {formValues.geolocationCheck && (
                      <div style={{ marginTop: 10 }}>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'row',
                          flexWrap: 'wrap',
                          gap: 10,
                          marginBottom: 10,
                        }}
                        >
                          <Field
                            name="geolocation.latitude"
                            type="text"
                            component={CustomField}
                            title={<Entity entity="latitude" />}
                            placeholder={L20NContext.getSync('insertLatitude')}
                            disabled={rulesFieldsDisabled}
                            containerStyle={{ width: 200, marginBottom: 0 }}
                            onInputBlur={value => this.onGeolocationCoordinateBlur('geolocation.latitude', value)}
                          />
                          <Field
                            name="geolocation.longitude"
                            type="text"
                            component={CustomField}
                            title={<Entity entity="longitude" />}
                            placeholder={L20NContext.getSync('insertLongitude')}
                            disabled={rulesFieldsDisabled}
                            containerStyle={{ width: 200, marginBottom: 0 }}
                            onInputBlur={value => this.onGeolocationCoordinateBlur('geolocation.longitude', value)}
                          />
                        </div>
                        <Field
                          name="geolocation"
                          component={MapSelectionField}
                          disabled={rulesFieldsDisabled}
                        />
                      </div>
                    )}
                  </div>
                )}
              </FormFlatContainer>
            </div>

            <MDButton
              title={<Entity entity="save" />}
              backgroundColor={BLUE}
              containerStyle={{ width: '100%', margin: 0, marginTop: 20 }}
              style={{ height: 45, borderRadius: 0 }}
              disabled={rulesFieldsDisabled}
              onClick={() => this.onSaveAutomationRules()}
            />
          </div>
        </div>
      </FormFlatContainer>
    );
  }
}

export default PowerControlUnitAutomationView;
